import { FC, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import toast, { Toaster } from "react-hot-toast";
import InputTemplate from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useNavigate } from "react-router-dom";
import { Input } from "@material-tailwind/react";
import { ReactComponent as EyeOpen } from "../../../images/eye.svg";
import { ReactComponent as EyeClosed } from "../../../images/eyeClosed.svg";
import { NoSearchEngineTemplate } from "templates/NoSearchEngineTemplate";
import { ReactComponent as LeftIcon } from "../../../images/leftIcon.svg";
import { PublicRoute } from "routers/PublicRoute";
import { resetPassword } from "services/forgot-password/reset-password";

export interface PageResetPasswordProps {}

interface ResetPasswordProps {
  className?: string;
}

export const PageResetPassword: FC<PageResetPasswordProps> = ({
  className = "",
}: ResetPasswordProps) => {
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({
    password: false,
    newPassword: false,
    code: false,
  });
  const navigate = useNavigate();
  const validationSchema = yup.object({
    email: yup
      .string()
      .email("Insira um e-mail válido")
      .required("E-mail é obrigatório"),
    code: yup.number().required("Código de verificação é obrigatório"),
    password: yup.string().required("Senha é obrigatória"),
    confirmPassword: yup
      .string()
      .test("passwords-match", "As senhas não coincidem", function (value) {
        return value === this.parent.password || value === null;
      })
      .required("Confirmação de senha é obrigatória"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      code: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const res = resetPassword({
        email: values.email,
        code: values.code,
        password: values.password,
      });
      toast.promise(
        res,
        {
          loading: "Atualizando senha...",
          success: (res) => {
            setTimeout(() => {
              navigate("/");
            }, 3000);
            return "Sua senha foi atualizada com sucesso!";
          },
          error: (error) => {
            if (error instanceof Error) {
              toast.error(error.message);
              return error.message;
            }

            if (error === "Failed to fetch") {
              toast.error(
                "Tivemos um erro ao atualizar a senha. Tente novamente."
              );
              return "Tivemos um erro ao atualizar a senha. Tente novamente.";
            }

            toast.error(
              "Tivemos um erro ao atualizar a senha. Tente novamente."
            );
            return "Tivemos um erro ao atualizar a senha. Tente novamente.";
          },
        },
        {
          style: {
            padding: "18px",
            fontSize: "16px",
          },
          success: {
            duration: 8000,
          },
        }
      );
    },
  });

  const handleClickShowPassword = (field: string) => {
    setShowPassword((prevShowPassword) => ({
      ...prevShowPassword,
      [field]: !prevShowPassword[field],
    }));
  };

  const isPasswordValid = (password: string) => {
    const minLength = password.length >= 8;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    return (
      minLength && hasSpecialChar && hasUpperCase && hasLowerCase && hasNumber
    );
  };

  return (
    <PublicRoute>
      <NoSearchEngineTemplate>
        <button
          className="flex flex-row items-center ml-5 mt-5 cursor-pointer"
          onClick={() => navigate(-1)}
        >
          <LeftIcon style={{ marginRight: "10px" }} />
          <span className="text-xl">Voltar</span>
        </button>
        <div
          className={`nc-PageResetPassword ${className}`}
          data-nc-id="PageResetPassword"
        >
          <Toaster />
          <div className="container mb-24 lg:mb-32">
            <h2 className="my-10 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
              Redefinir Senha
            </h2>
            <div className="max-w-md mx-auto space-y-6">
              <form
                className="grid grid-cols-1 gap-6"
                onSubmit={formik.handleSubmit}
              >
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Endereço de e-mail
                  </span>
                  <InputTemplate
                    type="email"
                    id="email"
                    placeholder="E-mail"
                    className={`mt-1 mb-1 ${
                      formik.touched.email && formik.errors.email
                        ? "focus:ring-red-500 focus:border-opacity-50 focus:border-red-500 border-red-500"
                        : ""
                    }`}
                    {...formik.getFieldProps("email")}
                  />
                  {formik.touched.email && formik.errors.email ? (
                    <div className="text-red-500 text-xs">
                      {formik.errors.email}
                    </div>
                  ) : null}
                </label>
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Código de verificação
                  </span>
                  <Input
                    type={showPassword["code"] ? "text" : "password"}
                    id="code"
                    placeholder="Código de verificação"
                    className={`mt-1 mb-1 ${
                      formik.touched.code && formik.errors.code
                        ? "focus:ring-red-500 focus:border-opacity-50 focus:border-red-500 border-red-500"
                        : ""
                    }`}
                    {...formik.getFieldProps("code")}
                    icon={
                      showPassword["code"] ? (
                        <EyeClosed
                          onClick={() => handleClickShowPassword("code")}
                          className="mt-3 cursor-pointer"
                        />
                      ) : (
                        <EyeOpen
                          onClick={() => handleClickShowPassword("code")}
                          className="mt-3 cursor-pointer"
                        />
                      )
                    }
                  />
                  {formik.touched.code && formik.errors.code ? (
                    <div className="text-red-500 text-xs">
                      {formik.errors.code}
                    </div>
                  ) : null}
                </label>
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Senha
                  </span>
                  <Input
                    type={showPassword["password"] ? "text" : "password"}
                    id="password"
                    placeholder="Senha"
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onDrag={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                    className={`mt-1 mb-1 ${
                      formik.touched.password && formik.errors.password
                        ? "focus:ring-red-500 focus:border-opacity-50 focus:border-red-500 border-red-500"
                        : ""
                    }`}
                    {...formik.getFieldProps("password")}
                    icon={
                      showPassword["password"] ? (
                        <EyeClosed
                          onClick={() => handleClickShowPassword("password")}
                          className="mt-3 cursor-pointer"
                        />
                      ) : (
                        <EyeOpen
                          onClick={() => handleClickShowPassword("password")}
                          className="mt-3 cursor-pointer"
                        />
                      )
                    }
                  />
                  {formik.touched.password && formik.errors.password ? (
                    <div className="text-red-500 text-xs">
                      {formik.errors.password}
                    </div>
                  ) : null}
                </label>
                <div className="ml-4 text-green-500">
                  <span
                    className={`${
                      isPasswordValid(formik.values.password)
                        ? ""
                        : "text-red-500"
                    }`}
                  >
                    Tamanho mínimo de 8 caracteres
                  </span>
                  <br />
                  <span
                    className={`${
                      /[!@#$%^&*(),.?":{}|<>]/.test(formik.values.password)
                        ? ""
                        : "text-red-500"
                    }`}
                  >
                    Deve conter pelo menos 1 caractere especial
                  </span>
                  <br />
                  <span
                    className={`${
                      /[A-Z]/.test(formik.values.password) ? "" : "text-red-500"
                    }`}
                  >
                    Deve conter pelo menos 1 letra maiúscula
                  </span>
                  <br />
                  <span
                    className={`${
                      /[a-z]/.test(formik.values.password) ? "" : "text-red-500"
                    }`}
                  >
                    Deve conter pelo menos 1 letra minúscula
                  </span>
                  <br />
                  <span
                    className={`${
                      /[0-9]/.test(formik.values.password) ? "" : "text-red-500"
                    }`}
                  >
                    Deve conter pelo menos 1 número
                  </span>
                </div>
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Confirmar Senha
                  </span>
                  <Input
                    type={
                      showPassword["confirmNewPassword"] ? "text" : "password"
                    }
                    id="confirmPassword"
                    placeholder="Confirmar Senha"
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onDrag={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                    className={`mt-1 mb-1 ${
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                        ? "focus:ring-red-500 focus:border-opacity-50 focus:border-red-500 border-red-500"
                        : ""
                    }`}
                    {...formik.getFieldProps("confirmPassword")}
                    icon={
                      showPassword["confirmNewPassword"] ? (
                        <EyeClosed
                          onClick={() =>
                            handleClickShowPassword("confirmNewPassword")
                          }
                          className="mt-3 cursor-pointer"
                        />
                      ) : (
                        <EyeOpen
                          onClick={() =>
                            handleClickShowPassword("confirmNewPassword")
                          }
                          className="mt-3 cursor-pointer"
                        />
                      )
                    }
                  />
                  {formik.touched.confirmPassword &&
                  formik.errors.confirmPassword ? (
                    <div className="text-red-500 text-xs">
                      {formik.errors.confirmPassword}
                    </div>
                  ) : null}
                </label>
                <ButtonPrimary
                  type="submit"
                  disabled={
                    !formik.isValid || !formik.getFieldProps("email").value
                  }
                  className="bg-[--blue] hover:bg-[--blue-dark] disabled:bg-[#b2d0e0] disabled:cursor-not-allowed"
                >
                  Continue
                </ButtonPrimary>
              </form>
            </div>
          </div>
        </div>
      </NoSearchEngineTemplate>
    </PublicRoute>
  );
};
